import { Component, ElementRef, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { AuthService } from '../../../services/auth.service';
import { MTMFileDownloadService, onDownloadStart } from '../mtm-file-download.service';
import { FileTransferService, TransferStatus } from '../../../services/signed-url';
import { MTMFileDownload } from '../../../services/signed-url/mtm-file-download';
import { SignedUrlService } from '../../../services/signedurl.service';
import { GoogleAnalyticsService } from '../../../services/google-analytics.service';
import { EmitterService } from '../../../services/emitter.service';
import { Subject, takeUntil } from 'rxjs';
import { FileUtilsService } from '../../../services/file-utils.service';
import { FileTypeService } from '../../../services/file-type.service';
import { FileTranscodeInfo } from '../../../interfaces/file-tree.interface';

export interface VideoDownloadOption {
  label: string;
  badge?: string;
  detail: string;
  quality?: string;
  type: 'original' | 'video' | 'gif' | 'audio';
}

const VIDEO_DOWNLOAD_IGNORE_QUALITIES = new Set([
  '_SMALL_211_121',
  '_SMALL_209_241',
  '_TINY_211_121',
  '_TINY_209_241',
  '_TINY',
  '_SMALL',
]);

@Component({
  selector: 'mtm-file-download-control',
  templateUrl: './mtm-file-download-control.component.html',
  styleUrl: './mtm-file-download-control.component.scss',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class MTMFileDownloadControl implements OnInit, OnDestroy {
  @Input() file: any;
  @Input() displayType: any = 'compact';
  @Input() uniqueControlId: any;
  @Input() iconSize: number = 32;
  @Input() showUploadTime: boolean = false;
  @Input() lightIcon: boolean = false;
  @Input() iconColor: string = 'red';
  @Input() iconUrl: string;
  @Input() isFullscreen = false;
  @Input() fileDownload?: boolean = true;
  authUserSettings: any;
  dateFormat: 'MMMM dd, yyyy';

  transferStatus = TransferStatus;

  fileDownloadInfo: MTMFileDownload;

  ngUnsubscribe = new Subject();

  constructor(
    private readonly elementRef: ElementRef,
    private readonly mtmFileDownloadService: MTMFileDownloadService,
    private readonly authService: AuthService,
    private readonly transferService: FileTransferService,
    private readonly signedUrlService: SignedUrlService,
    private readonly fileUtilsService: FileUtilsService,
    private readonly googleAnalyticsService: GoogleAnalyticsService,
    private readonly fileTypeService: FileTypeService
  ) {
    this.authUserSettings = this.authService.getAuthUserSettings();
    if (this.authUserSettings.dateFormat) {
      this.dateFormat = this.authUserSettings.dateFormat.replace('YYYY', 'yyyy').replace('DD', 'dd');
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  ngOnInit(): void {
    this.elementRef.nativeElement.setAttribute('id', this.uniqueControlId);
    EmitterService.get('FILE:DOWNLOAD')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res) => {
          if (this.file.id == res.id) {
            this.downloadFile();
          }
        },
      });
  }

  private sendDownloadGAEvent() {
    if (!this.file) {
      return;
    }

    try {
      let fileName = this.file?.displayName || this.file?.name;
      if (fileName) {
        fileName = this.fileUtilsService.getFileDisplayName(fileName);
      }

      const downloadEvent: any = {
        fileName,
        fileUrl: this.file.signedURL,
        fileType: this.file?.contentType,
        fileId: this.file?.id,
        projectId: this.file?.projectId,
        downloadTime: new Date().toISOString(),
        user: this.authService.getAuthUserName(),
      };

      if (this.file?.sectionId) {
        downloadEvent.sectionId = this.file.sectionId;
      }

      if (this.file?.subSectionId) {
        downloadEvent.subSectionId = this.file.subSectionId;
      }

      if (this.file?.sourceId) {
        downloadEvent.sourceId = this.file.sourceId;
      }

      if (this.file?.versionNumber) {
        downloadEvent.versionNumber = this.file.versionNumber;
      }

      console.log('file info for download', this.file, downloadEvent);
      this.googleAnalyticsService.sendEvent('file_download', downloadEvent);
    } catch (error) {
      console.error('Error sending download event to GA', error);
    }
  }

  private performDownload() {
    onDownloadStart.emit();
    //this.fileDownloadInfo = new MTMFileDownloadModel(this.uniqueControlId, this.file);
    this.fileDownloadInfo = MTMFileDownload.fromFileRef(this.file);
    this.fileDownloadInfo.id = this.uniqueControlId;
    this.fileDownloadInfo.url = this.file.signedURL;
    this.transferService.downloadFile(this.fileDownloadInfo);
    this.sendDownloadGAEvent();
  }

  get showVideoDropdown(): boolean {
    if (!this.file) return false;
    return this.fileTypeService.isVideoFile(this.file) && this.videoDownloadOptions.length > 0;
  }

  get videoDownloadOptions(): VideoDownloadOption[] {
    if (!this.file?.transcodeMetadata?.length) return [];
    const meta: FileTranscodeInfo[] = this.file.transcodeMetadata;
    const videoMeta = meta.filter(
      (m) => m?.quality && !VIDEO_DOWNLOAD_IGNORE_QUALITIES.has(m.quality) && m.codec !== 'jpeg' && m.codec !== 'jpg'
    );
    if (videoMeta.length === 0) return [];

    const options: VideoDownloadOption[] = [];
    const first = videoMeta[0];
    const res = first.resolution || '';
    const codec = (first.codec || '').toUpperCase();
    const is4K = /3840|2160|4K/i.test(res) || (first.quality && /4K|2160/i.test(first.quality));
    options.push({
      label: 'Original Quality',
      badge: is4K ? '4K' : undefined,
      detail: [res, codec].filter(Boolean).join(' · ') || codec || res,
      type: 'original',
    });

    videoMeta.slice(1).forEach((m) => {
      const resLabel = this.qualityToLabel(m.quality);
      const detail = [m.resolution, (m.codec || '').toUpperCase()].filter(Boolean).join(' · ') || m.codec || '';
      const badge = resLabel?.includes('1080') ? 'HD' : undefined;
      options.push({
        label: resLabel || m.resolution || m.quality || 'Video',
        badge,
        detail,
        quality: m.quality,
        type: 'video',
      });
    });

    const gifMeta = meta.find((m) => m?.codec?.toLowerCase() === 'gif');
    if (gifMeta) {
      options.push({
        label: 'GIF Preview',
        badge: '.GIF',
        detail: '',
        quality: gifMeta.quality,
        type: 'gif',
      });
    }

    const audioMeta = meta.find((m) => m?.codec?.toLowerCase() === 'wav' || m?.codec?.toLowerCase() === 'audio');
    if (audioMeta) {
      options.push({
        label: 'Audio Only',
        badge: '.WAV',
        detail: '',
        quality: audioMeta.quality,
        type: 'audio',
      });
    }

    return options;
  }

  private qualityToLabel(quality: string | undefined): string {
    if (!quality) return '';
    const q = quality.startsWith('_') ? quality.slice(1) : quality;
    if (/4K|2160/i.test(q)) return '4K';
    if (/1080|LARGE/i.test(q)) return '1080p Full HD';
    if (/720|MEDIUM/i.test(q)) return '720p HD';
    if (/480|SMALL/i.test(q)) return '480p SD';
    return q + 'p';
  }

  buildVideoDownloadUrl(quality?: string): string {
    if (!this.file?.signedURL) return '';
    const params: string[] = [];
    if (this.file.sectionId) params.push('sectionId=' + this.file.sectionId);
    if (this.file.subSectionId) params.push('subSectionId=' + this.file.subSectionId);
    if (quality) params.push('thumbnail=' + quality);
    const query = params.length > 0 ? '?' + params.join('&') : '';
    return this.file.signedURL + query;
  }

  downloadWithQuality(option: VideoDownloadOption, dropdown: { close: () => void }) {
    if (!this.fileDownload) return;
    const doDownload = (url: string) => {
      onDownloadStart.emit();
      this.fileDownloadInfo = MTMFileDownload.fromFileRef(this.file);
      this.fileDownloadInfo.id = this.uniqueControlId;
      this.fileDownloadInfo.url = url;
      if (option.quality) {
        const base = (this.file.displayName || this.file.name || '').split('.').slice(0, -1).join('.');
        this.fileDownloadInfo.displayName = (base || 'video') + '.mp4';
      }
      this.transferService.downloadFile(this.fileDownloadInfo);
      this.sendDownloadGAEvent();
    };
    if (this.file.signedURL) {
      const url = this.buildVideoDownloadUrl(option.quality);
      doDownload(url);
    } else {
      this.signedUrlService.getObjectStorageInfo(this.file.id).subscribe({
        next: (response: any) => {
          if (response) {
            this.file.signedURL = response.signedURL;
            const url = this.buildVideoDownloadUrl(option.quality);
            doDownload(url);
          }
        },
      });
    }
    dropdown.close();
  }

  downloadFile() {
    if (this.fileDownload) {
      if (this.file.signedURL) {
        this.performDownload();
      } else {
        this.signedUrlService.getObjectStorageInfo(this.file.id).subscribe({
          next: (response: any) => {
            if (response) {
              this.file.signedURL = response.signedURL;
              this.performDownload();
            }
          },
        });
      }
    }
  }
}
